import {
  Component,
  OnInit,
  OnChanges,
  Output,
  EventEmitter,
  Input,
  SimpleChanges,
  ChangeDetectorRef,
} from '@angular/core';
import { Router, Event, NavigationEnd, ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';
import { Map } from 'mapbox-gl';
import { first } from 'rxjs/operators';
import { GeoPoint } from '../../geopoint';

import { environment } from 'src/environments/environment';
import { FirebaseService } from '../../firebase.service';

declare var mapboxgl: any;

interface PointData {
  title: string;
  description: string;
  imgs: Array<string>;
  audio: string;
  threshold: number;
  coordinates: any;
  slug: string;
  stop_number: string;
}

@Component({
  selector: 'app-map',
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss'],
})
export class MapComponent implements OnInit, OnChanges {
  @Output() arrived = new EventEmitter<boolean>();
  @Input() stops: Array<any>;
  @Input() stopNumber: number;
  @Input() state: string;
  @Input() language: string = 'en';
  @Output() trackUserLocation = new EventEmitter<boolean>(true);

  map: any;
  url_location: string;
  user_location_status: string;
  user_coordinates: [number, number];
  threshold: number;
  geolocate: any;
  attributionControl: any;
  current_slug: string;
  user_lat: number = 0;
  user_lng: number = 0;
  lat_check: number = 0;
  lng_check: number = 0;
  lat_check1: number;
  lat_check2: number;
  lng_check1: number;
  lng_check2: number;
  check1: string;
  check2: string;
  update_count: number = 0;
  stop_number: number = 0;
  next_coordinates: any;
  arrive_button: boolean = true;
  visited_points: any;
  next_point: any;
  cur_point: any;
  all_points: Array<PointData> = [];
  begin_tour: boolean = true;
  check_location_timeout: any;
  popup: any;
  wasInfo: boolean = false;
  welcomeToMapBox: boolean = true;

  locationServicesOff: boolean = false;
  revealIOSInstructions: boolean = false;

  timeoutBuffer: any;

  paths: Array<any> = [];

  cur_stop: PointData;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private location: Location,
    private firebase_service: FirebaseService,
    private changeRef: ChangeDetectorRef
  ) {
    this.router.events.subscribe((val) => {
      if (val instanceof NavigationEnd) {
        // console.log(val);
      }
    });
    this.route.fragment.subscribe((val) => {
      if (val == 'iOSInstructions') {
        // console.log(val);
        this.revealIOSInstructions = true;
      } else {
        this.revealIOSInstructions = false;
      }
    });
  }

  ngOnInit(): void {
    mapboxgl.accessToken =
      'pk.eyJ1IjoibWF4aW11czg3NTQiLCJhIjoiY2tncmVxYm13MTI5aTJ4cDM3Nm1kZHd2ZiJ9.2j5T5zECqFQtjU47BzSlrw';
    this.map = new mapboxgl.Map({
      container: 'map', // container id
      style: 'mapbox://styles/maximus8754/ckgrhdjdw08iw19ql2djh2e9h',
      center: [
        this.stops[this.stopNumber].coordinates.longitude,
        this.stops[this.stopNumber].coordinates.latitude,
      ], // starting position
      zoom: 16, // starting zoom
      dragPan: {
        linearity: 0.1,
        // easing: "bezier(0, 0, 0.3, 1)",
        maxSpeed: 1,
        deceleration: 200,
      },
    });
    this.map.scrollZoom.setZoomRate(1 / 1000);
    this.geolocate = new mapboxgl.GeolocateControl({
      positionOptions: {
        enableHighAccuracy: true,
      },
      trackUserLocation: true,
      showUserHeading: true,
    });
    this.geolocate.on('error', (e) => {
      this.trackUserLocation.emit(false);
      this.locationServicesOff = true;
      this.changeRef.detectChanges();
    });
    this.attributionControl = new mapboxgl.AttributionControl({
      compact: true,
    });
    this.map.on('load', this.mapLoaded);
    this.map.addControl(this.geolocate);

    this.firebase_service
      .getPaths()
      .pipe(first())
      .subscribe((dataSnap) => {
        dataSnap.forEach((snap) => {
          const snapData = snap.payload.doc.data();
          // console.log(snapData.coordinates);
          this.paths.push(snapData.coordinates);
        });
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    console.log('changes', changes);
    if (changes.stopNumber) {
      if (changes.stopNumber.currentValue != 0) {
        if (this.popup) {
          this.popup.remove();
        }
      }
      if (!changes.stopNumber.firstChange) {
        this.next_point = {
          type: 'FeatureCollection',
          features: [
            {
              type: 'Feature',
              geometry: {
                type: 'Point',
                coordinates: [
                  this.stops[changes.stopNumber.currentValue].coordinates
                    .longitude,
                  this.stops[changes.stopNumber.currentValue].coordinates
                    .latitude,
                ],
              },
            },
          ],
        };
        this.map.getSource('next-point').setData(this.next_point);
        this.checkLocation();
        if (
          changes.stopNumber.currentValue !=
          changes.stopNumber.previousValue + 1
        ) {
          //User set set new stop
          this.map.getSource('current-point').setData({
            type: 'FeatureCollection',
            features: [
              {
                type: 'Feature',
                geometry: {
                  type: 'Point',
                  coordinates: [],
                },
              },
            ],
          });
          this.map.getSource('line-animation').setData({
            type: 'Feature',
            geometry: {
              type: 'LineString',
              coordinates: [],
            },
          });
          this.map.flyTo({
            center: [
              this.stops[changes.stopNumber.currentValue].coordinates.longitude,
              this.stops[changes.stopNumber.currentValue].coordinates.latitude,
            ],
            essential: true, // this animation is considered essential with respect to prefers-reduced-motion
            speed: 1,
            zoom: 16,
          });
        } else {
          if (changes.stopNumber.currentValue > 0) {
            this.map.getSource('current-point').setData({
              type: 'FeatureCollection',
              features: [
                {
                  type: 'Feature',
                  geometry: {
                    type: 'Point',
                    coordinates: [
                      this.stops[changes.stopNumber.previousValue].coordinates
                        .longitude,
                      this.stops[changes.stopNumber.previousValue].coordinates
                        .latitude,
                    ],
                  },
                },
              ],
            });
            setTimeout(() => {
              this.nextLocation();
            }, 1000);
          }
        }
      } else {
        console.log('FIRST CHANGE: ' + changes.stopNumber.currentValue);
        console.log(
          changes.stops.currentValue[changes.stopNumber.currentValue]
        );
        console.log(this.stops[changes.stopNumber.currentValue]);
      }
    }
    if (changes.state) {
      if (!changes.state.firstChange) {
        console.log(changes.state.currentValue);
        if (changes.state.currentValue == 'arrived') {
          if (this.stopNumber == 0) {
            // this.popup.remove();
          }
          if (changes.state.currentValue == 'continue') {
          }
          this.hasArrived();
          this.cur_point = {
            type: 'FeatureCollection',
            features: [
              {
                type: 'Feature',
                geometry: {
                  type: 'Point',
                  coordinates: [
                    this.stops[this.stopNumber].coordinates.longitude,
                    this.stops[this.stopNumber].coordinates.latitude,
                  ],
                },
              },
            ],
          };
          this.map.getSource('current-point').setData(this.cur_point);
          this.map.flyTo({
            center: [
              this.stops[this.stopNumber].coordinates.longitude,
              this.stops[this.stopNumber].coordinates.latitude,
            ],
            essential: true, // this animation is considered essential with respect to prefers-reduced-motion
            speed: 1,
            zoom: 17,
          });
        }
      }
    }
  }

  begin = () => {
    if (this.map.loaded()) {
    }
  };

  mapLoaded = () => {
    let markerTxt = 'Starting Location';
    if (this.language == 'es') {
      markerTxt = 'Ubicación de Inicio';
    }
    if (this.stopNumber == 0) {
      var markerHeight = 10,
        markerRadius = 5,
        linearOffset = 0;
      var popupOffsets = {
        top: [0, 5],
        'top-left': [0, 0],
        'top-right': [0, 0],
        bottom: [0, -markerHeight],
        'bottom-left': [
          linearOffset,
          (markerHeight - markerRadius + linearOffset) * -1,
        ],
        'bottom-right': [
          -linearOffset,
          (markerHeight - markerRadius + linearOffset) * -1,
        ],
        left: [markerRadius, (markerHeight - markerRadius) * -1],
        right: [-markerRadius, (markerHeight - markerRadius) * -1],
      };
      this.popup = new mapboxgl.Popup({
        offset: popupOffsets,
        className: 'map-popup',
        closeButton: false,
        closeOnClick: false,
      })
        .setLngLat([-122.4577104371773, 38.292676255922544])
        .setHTML('<p class="mb-0">' + markerTxt + '</p>')
        .setMaxWidth('300px')
        .addTo(this.map);
    }
    const start_point = [
      this.stops[this.stopNumber].coordinates.longitude,
      this.stops[this.stopNumber].coordinates.latitude,
    ];
    this.lng_check = this.stops[this.stopNumber].coordinates.longitude;
    this.lat_check = this.stops[this.stopNumber].coordinates.latitude;
    let geojson = {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: [start_point],
          },
        },
      ],
    };
    this.map.addSource('line', {
      type: 'geojson',
      data: geojson,
    });
    this.map.addLayer({
      id: 'line-animation',
      type: 'line',
      source: {
        type: 'geojson',
        data: geojson,
      },
      layout: {
        'line-cap': 'round',
        'line-join': 'bevel',
      },
      paint: {
        'line-color': '#27ac06',
        'line-width': 7,
      },
    });
    this.next_point = {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [
              this.stops[this.stopNumber].coordinates.longitude,
              this.stops[this.stopNumber].coordinates.latitude,
            ],
          },
        },
      ],
    };
    this.map.addSource('next-point', {
      type: 'geojson',
      data: this.next_point,
    });
    this.map.addLayer({
      id: 'next',
      type: 'circle',
      source: 'next-point',
      paint: {
        // make circles larger as the user zooms from z12 to z22
        'circle-radius': {
          stops: [
            [14, 5],
            [17, 10],
            [19, 16],
            [22, 21],
          ],
        },
        'circle-color': '#da705d',
        'circle-stroke-color': '#f4f4c7',
        'circle-stroke-width': 2,
      },
    });
    this.cur_point = {
      type: 'FeatureCollection',
      features: [],
    };
    this.map.addSource('current-point', {
      type: 'geojson',
      data: this.cur_point,
    });
    this.map.addLayer({
      id: 'current',
      type: 'circle',
      source: 'current-point',
      paint: {
        'circle-radius': {
          stops: [
            [14, 5],
            [17, 10],
            [19, 16],
            [22, 21],
          ],
        },
        'circle-color': '#5B934E',
        'circle-stroke-color': '#f4f4c7',
        'circle-stroke-width': 2,
      },
    });
    this.geolocate.once('geolocate', (e) => {
      this.trackUserLocation.emit(false);
      console.log(e);
      let coords = [e.coords, this.stops[this.stopNumber].coordinates];
      this.centerMap(coords, 30, true);
    });
    setTimeout(() => {
      this.geolocate.trigger();
    }, 2000);
    this.checkLocation();
    // console.log("MAP LOADED");
    // this.firebase_service.getAllPoints().subscribe(data => {
    //   console.log("FIREBASE DATA");
    //   for (var i=0; i<data.docs.length; i++) {
    //     this.all_points.push(data.docs[i].data());
    //   }
    //   this.checkURL();
    //   this.next_point = {
    //     "type": "Feature",
    //     "geometry": {
    //       "type": "Point",
    //       "coordinates": []
    //     }
    //   }
    //   this.visited_points = {
    //     'type': 'FeatureCollection',
    //     'features': [
    //       {
    //         'type': 'Feature',
    //         'geometry': {
    //           'type': 'Point',
    //           'coordinates': []
    //         }
    //       }
    //     ]
    //   };
    // });
    var addFeatures = () => {};
    addFeatures();
    //   this.map.addSource('visited-points', {
    //     type: 'geojson',
    //     data: this.visited_points
    //   });
    // this.map.addLayer({
    //   'id': 'visited',
    //   'type': 'circle',
    //   'source': 'visited-points',
    //   'paint': {
    //   // make circles larger as the user zooms from z12 to z22
    //     'circle-radius': {
    //       'stops': [
    //         [14, 5],
    //         [17, 10],
    //         [19, 16],
    //         [22, 21]
    //       ]
    //     },
    //     'circle-color': "#27ac06"
    //   }
    // });
    // }
  };

  checkLocation = () => {
    var state = this.geolocate._watchState;
    // console.log(this.next_stop);
    this.changeRef.markForCheck();
    const check_point = this.stops[this.stopNumber];
    // console.log(check_point);
    if (state != 'OFF') {
      // console.log(state);
      this.changeRef.markForCheck();
      if (state == 'ACTIVE_LOCK' || state == 'BACKGROUND') {
        this.changeRef.markForCheck();
        // console.log(this.user_lat = this.map._controls);
        this.user_lat = this.geolocate._userLocationDotMarker.getLngLat().lat;
        this.user_lng = this.geolocate._userLocationDotMarker.getLngLat().lng;

        // console.log("Lat: " + check_point.coordinates.latitude);
        // console.log("Lng: " + check_point.coordinates.longitude);
        // console.log(this.map._controls[2]._userLocationDotMarker.getLngLat());

        if (
          check_point.coordinates.latitude - check_point.threshold <=
            this.user_lat &&
          this.user_lat <=
            check_point.coordinates.latitude + check_point.threshold
        ) {
          this.check1 = 'Pass Lat Check';
          if (
            check_point.coordinates.longitude - check_point.threshold <=
              this.user_lng &&
            this.user_lng <=
              check_point.coordinates.longitude + check_point.threshold
          ) {
            this.check2 = 'Pass Lng Check';
            // if(document.getElementById("Arrived")) {
            //   document.getElementById("Arrived").classList.remove('disable');
            // }

            this.arrived.emit(true);
          } else {
            // if(document.getElementById("Arrived")) {
            //   document.getElementById("Arrived").classList.add('disable');
            // }
            this.check2 = '';
            // clearTimeout(this.timeoutBuffer);
            // console.log("LNG CHECK");
            this.timeoutBuffer = setTimeout(this.checkLocation, 3000);
            this.changeRef.markForCheck();
          }
        } else {
          this.check1 = '';
          // if(document.getElementById("Arrived")) {
          //   document.getElementById("Arrived").classList.add('disable');
          // }
          // console.log("LAT CHECK");
          // clearTimeout(this.timeoutBuffer);
          this.timeoutBuffer = setTimeout(this.checkLocation, 3000);
          this.changeRef.markForCheck();
        }
      } else {
        //
        // console.log("STATE CHECK");
        // clearTimeout(this.timeoutBuffer);
        this.timeoutBuffer = setTimeout(this.checkLocation, 3000);
        this.changeRef.markForCheck();
      }
    } else {
      this.user_location_status = 'OFF';
    }
    // this.arrived.emit(true);
  };

  data: any;

  hasArrived = () => {
    this.map.getSource('next-point').setData({
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [],
          },
        },
      ],
    });
    if (this.map.getSource('line-animation')) {
      this.map.getSource('line-animation').setData({
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: [],
        },
      });
    }
  };

  centerMap = (
    coordinates: Array<GeoPoint>,
    padding: number,
    fromUser: boolean
  ) => {
    if (fromUser) {
      console.log('CENTER map)');
      console.log(coordinates);
      var bounds = coordinates.reduce(function (bounds, coord) {
        return bounds.extend([coord.longitude, coord.latitude]);
      }, new mapboxgl.LngLatBounds(
        [coordinates[0].longitude, coordinates[0].latitude],
        [coordinates[1].longitude, coordinates[1].latitude]
      ));
      this.map.fitBounds(bounds, {
        padding: { top: 50, bottom: 50, left: 50, right: 50 },
      });
    } else {
      let longSum = 0;
      let latSum = 0;
      for (var i = 0; i < coordinates.length; i++) {
        longSum += coordinates[i].longitude;
        latSum += coordinates[i].latitude;
      }
      let longAvg = longSum / coordinates.length;
      let latAvg = latSum / coordinates.length;
      this.map.flyTo({
        center: [longAvg, latAvg],
        essential: true,
        speed: 1,
        zoom: 17,
      });
    }
    // console.log(bounds);
    // var bounds = coordinates.reduce(function (bounds, coord) {
    //   console.log(coord);
    //   console.log(bounds);
    //   return bounds.extend(coord);
    // }, new mapboxgl.LngLatBounds(coordinates[0], coordinates[1]));
    // this.map.fitBounds(bounds, {
    //   padding: padding
    // });
  };

  nextLocation = async () => {
    // this.info_service.sendInfo(this.cur_stop);
    var drawLine = (start_point, path_coordinates) => {
      // console.log("drawLine:");
      var feature;
      var speedFactor = 50; // number of frames per longitude degree
      var animation; // to store and cancel the animation
      var startTime = 0;
      var progress = 0;
      let geojson = {
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            geometry: {
              type: 'LineString',
              coordinates: [start_point],
            },
          },
        ],
      };
      if (this.map.getSource('line-animation')) {
        // console.log("HELL");
        this.map.getSource('line-animation').setData({
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: [start_point],
          },
        });
        // console.log("After hell");
      } else {
        this.map.addSource('line', {
          type: 'geojson',
          data: geojson,
        });
        this.map.addLayer({
          id: 'line-animation',
          type: 'line',
          source: {
            type: 'geojson',
            data: geojson,
          },
          layout: {
            'line-cap': 'round',
            'line-join': 'bevel',
          },
          paint: {
            'line-color': '#27ac06',
            'line-width': 7,
          },
        });
        // this.map.moveLayer('line-animation', 'sonomatour');
      }
      var diffX = [];
      var diffY = [];
      for (var i = 0; i < path_coordinates.length - 1; i++) {
        diffX.push(
          Math.abs(
            Math.abs(path_coordinates[i].longitude) -
              Math.abs(path_coordinates[i + 1].longitude)
          )
        );
        diffY.push(
          Math.abs(
            Math.abs(path_coordinates[i].latitude) -
              Math.abs(path_coordinates[i + 1].latitude)
          )
        );
      }

      // var diffX = -122.434297 - 0;
      // var diffY = 38.278688 - 0;
      var sfX = [];
      var sfY = [];
      for (var iter = 0; iter < diffX.length; iter++) {
        // console.log("diffX: " + diffX[iter]);
        var distance = Math.sqrt(
          Math.pow(diffX[iter], 2) + Math.pow(diffY[iter], 2)
        );
        console.log(distance);
        var spf = distance * 100000;
        sfX.push(diffX[iter] / spf);
        sfY.push(diffY[iter] / spf);
      }

      // var sfX = diffX[0] / speedFactor;
      // var sfY = diffY[0] / speedFactor;

      var i = 0;
      var j = 0;

      var x = 0;
      var y = 0;
      var path_iter = 0;

      var lineCoordinates = [];

      while (
        Math.abs(i) < Math.abs(diffX[x]) ||
        Math.abs(j) < Math.abs(diffY[y])
      ) {
        lineCoordinates.push([
          path_coordinates[x].longitude + i,
          path_coordinates[x].latitude + j,
        ]);

        if (Math.abs(i) < Math.abs(diffX[x])) {
          if (
            path_coordinates[x + 1].longitude < path_coordinates[x].longitude
          ) {
            i -= sfX[x];
          } else {
            i += sfX[x];
          }
          if (Math.abs(i) >= Math.abs(diffX[x]) && x < diffX.length) {
            i = 0;
            x++;
            y++;
            j = 0;
          }
        }

        if (Math.abs(j) < Math.abs(diffY[y])) {
          if (path_coordinates[y + 1].latitude < path_coordinates[y].latitude) {
            j -= sfY[y];
          } else {
            j += sfY[y];
          }
        }
      }

      var animationCounter = 0;
      // progress = timestamp - startTime;
      var animateLine = () => {
        if (animationCounter < lineCoordinates.length) {
          geojson.features[0].geometry.coordinates.push(
            lineCoordinates[animationCounter]
          );
          this.map.getSource('line-animation').setData(geojson);
          animationCounter++;
          requestAnimationFrame(animateLine);
        } else {
          var coord = geojson.features[0].geometry.coordinates;
          coord.shift();
          if (coord.length > 0) {
            geojson.features[0].geometry.coordinates = coord;
            this.map.getSource('line-animation').setData(geojson);
            //-------------- Point2 Animation End ---------------
            console.log('END OF ANIMATION?');
            // this.arrived.emit(true);
          }
        }
      };
      animateLine();
    };
    // }

    this.map.on('sourcedata', function handler(e) {
      // console.log("source loaded");
      if (e.isSourceLoaded) {
        this.off('sourcedata', handler);
      }
    });
    // await this.firebase_service.getPathCoordinates(this.stop_number-1).subscribe(changes => {
    //   console.log(changes.payload.data().coordinates);
    //   const coordinates = changes.payload.data().coordinates;
    //   var start_point;
    //   var end_point;
    //   var padding = changes.payload.data().padding;
    //   start_point = [coordinates[0].longitude, coordinates[0].latitude];
    //   end_point = [coordinates[coordinates.length-1].longitude, coordinates[coordinates.length-1].latitude];
    //   console.log("before set data");
    //   this.map.getSource('next-point').setData( {
    //     "type": "FeatureCollection",
    //     "features": [{
    //       "type": "Feature",
    //       "geometry": {
    //         "type": "Point",
    //         "coordinates": [end_point[0], end_point[1]]
    //       }
    //     }]
    //   });

    // var center_coordinates = [start_point, end_point];

    const path_coordinates = this.paths[this.stopNumber - 1];
    console.log('PATH COORDINATES: ');
    console.log(path_coordinates);
    this.centerMap(path_coordinates, 30, false);
    const start_point = [
      this.paths[this.stopNumber - 1][0].longitude,
      this.paths[this.stopNumber - 1][0].latitude,
    ];
    drawLine(start_point, path_coordinates);
  };

  closeIOS() {
    this.revealIOSInstructions = false;
    let fragment = this.router.parseUrl(this.router.url).fragment;

    if (fragment == 'iOSInstructions') {
      let route = this.router.url.replace('#' + fragment, '');
      console.log(route);
      this.router.navigate([route]);
    }
  }

  CloseWelcomeToMapBox() {
    this.welcomeToMapBox = false;
  }
  
}
